import { Injectable, inject } from '@angular/core';
import { Either, Left, Right } from 'purify-ts';
import { ApiErrorCatalog } from '../../../domain/core/api-error';
import { Paginated } from '../../../domain/conversations/types';
import { TemplatesRepositoryFailure } from '../../../domain/templates/failure';
import { ITemplatesRepository } from '../../../domain/templates/i-templates-repository';
import {
  CreateTemplateProps,
  CreatedTemplate,
  DeleteTemplateProps,
  ListTemplatesOptions,
  Template,
} from '../../../domain/templates/types';
import { ApiResponse } from '../../../util/type';
import { IHttpClient } from '../../services/httpClient/interface';
import { withQuery } from '../util';
import { RawTemplate, mapTemplate } from './template-mapper';

const DUPLICATED_NAME_NAME = 'TemplateNameAlreadyExistsError';

@Injectable({
  providedIn: 'root',
})
export class RealTemplatesRepository implements ITemplatesRepository {
  private httpClient = inject(IHttpClient);

  public async getList(
    options: ListTemplatesOptions
  ): Promise<Either<TemplatesRepositoryFailure, Paginated<Template>>> {
    try {
      const url = withQuery('/whatsapp/templates', {
        status: options.status,
        cursor: options.cursor,
        limit: options.limit,
      });

      const { body } = await this.httpClient.get<ApiResponse<Paginated<RawTemplate>>>(url);
      return Right({ ...body.data, items: body.data.items.map(mapTemplate) });
    } catch (error) {
      return Left(
        TemplatesRepositoryFailure.fromApiError({
          error,
          fallbackMessage: 'Não foi possível carregar os templates.',
        })
      );
    }
  }

  public async create(
    props: CreateTemplateProps
  ): Promise<Either<TemplatesRepositoryFailure, CreatedTemplate>> {
    try {
      const { body } = await this.httpClient.post<ApiResponse<CreatedTemplate>>(
        '/whatsapp/templates',
        props
      );
      return Right(body.data);
    } catch (error) {
      if (ApiErrorCatalog.is(error, DUPLICATED_NAME_NAME)) {
        return Left(
          TemplatesRepositoryFailure.duplicatedName(ApiErrorCatalog.extractBody(error)?.message)
        );
      }

      return Left(
        TemplatesRepositoryFailure.fromApiError({
          error,
          fallbackMessage: 'Não foi possível criar o template.',
        })
      );
    }
  }

  public async remove(
    props: DeleteTemplateProps
  ): Promise<Either<TemplatesRepositoryFailure, void>> {
    try {
      const url = withQuery(`/whatsapp/templates/${encodeURIComponent(props.name)}`, {
        language: props.language,
      });

      await this.httpClient.delete(url);
      return Right(undefined);
    } catch (error) {
      return Left(
        TemplatesRepositoryFailure.fromApiError({
          error,
          fallbackMessage: 'Não foi possível excluir o template.',
        })
      );
    }
  }
}
